import {
  Cost,
  Customer,
  Order,
  OrderProduct,
  OrderStatus,
  StatusHistory,
  User,
  CUSTOMER_ORDER_STATUS_VALUES,
  getDisplayOrderNumber,
} from '../types/order';
import { DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS } from '../modules/orders/customerPayment';
import {
  asArray,
  asBoolean,
  asNumber,
  asOptionalArray,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import { normalizeFinancialCompleteness } from './financialCompleteness';
import { normalizeConversionUnit } from '../utils/dimensionConversion';
import {
  getPersistedQuantityForUnit,
  READABLE_QUANTITY_UNITS,
  type QuantityUnit,
} from '../utils/commercialQuantity';

export const ORDER_STATUSES: readonly OrderStatus[] = CUSTOMER_ORDER_STATUS_VALUES;

export const MAX_CUSTOMER_ORDER_NUMBER_LENGTH = 64;
export const CUSTOMER_ORDER_NUMBER_PATTERN = /^[A-Za-z0-9/_ .-]+$/;

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

export const customerOrderNumberRule = (t: TranslateFn) => ({
  max: MAX_CUSTOMER_ORDER_NUMBER_LENGTH,
  pattern: CUSTOMER_ORDER_NUMBER_PATTERN,
  message: t('orders:modal.validation.manualNumberInvalid', {
    max: MAX_CUSTOMER_ORDER_NUMBER_LENGTH,
  }),
});

const USER_ROLES = ['ADMIN', 'OPERATIONS', 'PARTNER', 'ORDER_STAFF', 'TESTER'] as const;

const normalizeVariantAttributes = (raw: unknown): Record<string, string> | undefined => {
  if (raw == null) return undefined;

  const attributes = asRecord(raw);
  if (!attributes) return undefined;

  return Object.fromEntries(
    Object.entries(attributes).flatMap(([rawKey, rawValue]) => {
      const key = rawKey.trim();
      const value = (
        typeof rawValue === 'string'
        || typeof rawValue === 'number'
        || typeof rawValue === 'boolean'
      ) ? String(rawValue).trim() : '';

      return key && value ? [[key, value]] : [];
    }),
  );
};

export interface OrderDto extends Order {
  commerciallyFrozenAt?: string | null;
  invoiceCoverageState?: string | null;
  customer: Customer | null;
  createdBy: User | null;
  accountManager?: User | null;
  customerConfirmation?: CustomerConfirmationCapability;
  customerStatusEmail?: CustomerStatusEmailCapability;
  costsLoaded: boolean;
  productsLoaded: boolean;
  statusHistoryLoaded: boolean;
  customerName: string;
  createdByName: string;
  accountManagerName: string;
  displayOrderNumber: string;
}

export interface CustomerConfirmationCapability {
  canSend: boolean;
  disabledReason: string | null;
}

export interface CustomerStatusEmailCapability {
  canSend: boolean;
  disabledReason: string | null;
}

/**
 * Supplier-order purchase-value hint for one customer-order line, returned by
 * GET /orders?includeSupplierPurchaseHints=true for users with
 * `canViewSupplierOrderPurchasePrices`. Two shapes share one type:
 *  - a concrete hint carries a numeric `unitPurchaseCostPLN` (+ provenance);
 *  - `ambiguous: true` marks a line backed by several supplier lines with
 *    conflicting unit costs — NO cost value, prefill is skipped.
 * See backend/src/services/supplierPurchaseHintService.js for the source shapes.
 */
export interface SupplierOrderLinkHintDto {
  orderProductId: string;
  ambiguous?: boolean;
  supplierOrderId?: string | null;
  supplierOrderDisplayNumber?: string | null;
  supplierOrderItemId?: string | null;
  // PLN-canonical unit purchase cost. Null when unresolved (kept for shape
  // predictability); a concrete prefill needs a finite positive value.
  unitPurchaseCostPLN?: number | null;
  // Exact unit cost entered on the supplier-order item, before PLN conversion.
  sourceUnitPurchaseCost?: number | null;
  // Whole-order link basis: full supplier-order purchase value assigned to this
  // single customer product, proportionally scaled by loaded quantity.
  purchaseValuePLN?: number | null;
  // Sum of original item values only when the whole supplier order has one
  // unambiguous source currency. Null for mixed-currency supplier orders.
  sourcePurchaseValue?: number | null;
  purchaseQuantity?: number | null;
  purchaseUnit?: string | null;
  exchangeRateToPLN?: number | null;
  exchangeRateToPLNSource?: string | null;
  exchangeRateToPLNTableNumber?: string | null;
  exchangeRateToPLNRequestedDate?: string | null;
  exchangeRateToPLNEffectiveDate?: string | null;
  sourceCurrency?: string | null;
  allocatedQuantity?: number | null;
  allocatedUnit?: string | null;
  // Unit used by the supplier line's unit purchase cost. This remains the
  // source of truth when legacy links do not carry an allocation unit.
  supplierOrderItemUnit?: string | null;
}

export interface OrderOptionDto {
  id: string;
  orderNumber: string;
  displayOrderNumber: string;
  clientOrderNumber?: string;
  customerName: string;
  label: string;
  status: OrderStatus;
  revenue: number;
  currency: string;
  description: string;
  // Route hints already returned by GET /orders — surfaced here so the transport
  // form can prefill loading/delivery stops from the selected orders.
  factoryLocation?: string | null;
  deliveryLocation?: string | null;
  customer: Customer | null;
  products: OrderProduct[];
  productsLoaded: boolean;
  // Optional supplier purchase-value hints (present only when the caller asked
  // for them AND the user may see supplier-order purchase prices). Absent for
  // everyone else — the transport form treats absence as "no prefill".
  supplierOrderLinks?: SupplierOrderLinkHintDto[];
}

export function normalizeOrder(raw: unknown, entity = 'order'): OrderDto {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  const customer = normalizeCustomer(record?.customer, `${entity}.customer`);
  const createdBy = normalizeUser(record?.createdBy, `${entity}.createdBy`);
  const accountManager = normalizeUser(record?.accountManager, `${entity}.accountManager`);
  const costs = asOptionalArray(
    record?.costs,
    (item, index) => normalizeCost(item, `${entity}.costs[${index}]`),
  );
  const products = asOptionalArray(
    record?.products,
    (item, index) => normalizeOrderProduct(item, `${entity}.products[${index}]`),
  );
  const statusHistory = asOptionalArray(
    record?.statusHistory,
    (item, index) => normalizeStatusHistory(item, `${entity}.statusHistory[${index}]`),
  );
  const customerConfirmationRecord = asRecord(record?.customerConfirmation);
  const customerStatusEmailRecord = asRecord(record?.customerStatusEmail);

  const order: OrderDto = {
    id: asString(record?.id),
    orderNumber: asString(record?.orderNumber),
    warehouseCostPending: typeof record?.warehouseCostPending === 'boolean' ? record.warehouseCostPending : undefined,
    costBasis: record?.costBasis === 'LEGACY' || record?.costBasis === 'WAREHOUSE_FIFO' ? record.costBasis : undefined,
    commerciallyFrozenAt: asOptionalString(record?.commerciallyFrozenAt) ?? null,
    invoiceCoverageState: asOptionalString(record?.invoiceCoverageState) ?? null,
    customerId: asString(record?.customerId),
    customer,
    createdById: asString(record?.createdById),
    createdBy,
    status: normalizeOrderStatus(record?.status, `${entity}.status`),
    allowedTransitions: asArray(record?.allowedTransitions, (item, index) => {
      if (typeof item === 'string' && ORDER_STATUSES.includes(item as OrderStatus)) {
        return item as OrderStatus;
      }
      reportDtoAnomaly(`${entity}.allowedTransitions[${index}]`, 'value', item);
      return null;
    }).filter((status): status is OrderStatus => Boolean(status)),
    manualOrderNumber: asOptionalString(record?.manualOrderNumber),
    useManualNumbering: asBoolean(record?.useManualNumbering),
    clientOrderNumber: asOptionalString(record?.clientOrderNumber),
    producer: asOptionalString(record?.producer),
    supplierId: asOptionalString(record?.supplierId),
    factoryLocation: asOptionalString(record?.factoryLocation),
    deliveryLocation: asOptionalString(record?.deliveryLocation),
    revenue: asNumber(record?.revenue),
    totalCost: record?.totalCost == null ? null : asNumber(record.totalCost),
    margin: record?.margin == null ? null : asNumber(record.margin),
    marginPercent: record?.marginPercent == null ? null : asNumber(record.marginPercent),
    financialCompleteness: normalizeFinancialCompleteness(
      record?.financialCompleteness,
      `${entity}.financialCompleteness`,
    ),
    // canViewMargin-only (stripped by responseFilter for others) → undefined when absent.
    cogsPLN: record?.cogsPLN == null ? undefined : asNumber(record?.cogsPLN),
    currency: asString(record?.currency, 'PLN'),
    exchangeRate: record?.exchangeRate == null ? undefined : asNumber(record?.exchangeRate),
    exchangeRateSource: asOptionalString(record?.exchangeRateSource),
    exchangeRateTableNumber: asOptionalString(record?.exchangeRateTableNumber),
    exchangeRateRequestedDate: asOptionalString(record?.exchangeRateRequestedDate),
    exchangeRateEffectiveDate: asOptionalString(record?.exchangeRateEffectiveDate),
    plannedRevenue: record?.plannedRevenue == null ? undefined : asNumber(record?.plannedRevenue),
    plannedTotalCost: record?.plannedTotalCost == null ? undefined : asNumber(record?.plannedTotalCost),
    plannedMargin: record?.plannedMargin == null ? undefined : asNumber(record?.plannedMargin),
    plannedMarginPercent: record?.plannedMarginPercent == null ? undefined : asNumber(record?.plannedMarginPercent),
    plannedMarginSnapshotAt: asOptionalString(record?.plannedMarginSnapshotAt),
    isSettled: record?.isSettled == null ? undefined : asBoolean(record?.isSettled),
    settledAt: asOptionalString(record?.settledAt),
    settledRevenue: record?.settledRevenue == null ? undefined : asNumber(record?.settledRevenue),
    settledTotalCost: record?.settledTotalCost == null ? undefined : asNumber(record?.settledTotalCost),
    settledMargin: record?.settledMargin == null ? undefined : asNumber(record?.settledMargin),
    settledMarginPercent: record?.settledMarginPercent == null ? undefined : asNumber(record?.settledMarginPercent),
    settlementStatus: record?.settlementStatus === 'SETTLED'
      || record?.settlementStatus === 'PARTIALLY_SETTLED'
      || record?.settlementStatus === 'OPEN'
      ? record.settlementStatus
      : undefined,
    proformaNumber: asOptionalString(record?.proformaNumber),
    proformaDate: asOptionalString(record?.proformaDate),
    invoiceNumber: asOptionalString(record?.invoiceNumber),
    invoiceDate: asOptionalString(record?.invoiceDate),
    waybillNumber: asOptionalString(record?.waybillNumber),
    waybillDate: asOptionalString(record?.waybillDate),
    customerPaymentDueDate: asOptionalString(record?.customerPaymentDueDate),
    customerPaymentStatus: record?.customerPaymentStatus === 'PAID' ? 'PAID' : 'PENDING',
    customerPaymentPaidAt: asOptionalString(record?.customerPaymentPaidAt),
    customerPaymentNotes: asOptionalString(record?.customerPaymentNotes),
    orderDate: asString(record?.orderDate),
    expectedDelivery: asOptionalString(record?.expectedDelivery),
    actualDelivery: asOptionalString(record?.actualDelivery),
    estimatedCompletionDate: asOptionalString(record?.estimatedCompletionDate),
    description: asString(record?.description),
    notes: asOptionalString(record?.notes),
    incoterms: asOptionalString(record?.incoterms),
    transportMode: record?.transportMode == null ? undefined : asNumber(record?.transportMode),
    deliveryCountry: asOptionalString(record?.deliveryCountry),
    dispatchCountry: asOptionalString(record?.dispatchCountry),
    transactionNature: record?.transactionNature == null ? undefined : asNumber(record?.transactionNature),
    isQuote: record?.isQuote == null ? undefined : asBoolean(record?.isQuote),
    quoteValidUntil: asOptionalString(record?.quoteValidUntil),
    quoteConvertedAt: asOptionalString(record?.quoteConvertedAt),
    accountManagerId: asOptionalString(record?.accountManagerId),
    accountManager,
    customerConfirmation: customerConfirmationRecord
      ? {
          canSend: asBoolean(customerConfirmationRecord.canSend),
          disabledReason: asOptionalString(customerConfirmationRecord.disabledReason) ?? null,
        }
      : undefined,
    customerStatusEmail: customerStatusEmailRecord
      ? {
          canSend: asBoolean(customerStatusEmailRecord.canSend),
          disabledReason: asOptionalString(customerStatusEmailRecord.disabledReason) ?? null,
        }
      : undefined,
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
    costs: costs ?? [],
    products: products ?? [],
    statusHistory: statusHistory ?? [],
    costsLoaded: costs !== undefined,
    productsLoaded: products !== undefined,
    statusHistoryLoaded: statusHistory !== undefined,
    customerName: customer?.name || '',
    createdByName: createdBy?.name || '',
    accountManagerName: accountManager?.name || createdBy?.name || '',
    displayOrderNumber: '',
  };

  order.displayOrderNumber = getDisplayOrderNumber(order);
  return order;
}

export function normalizeOrders(raw: unknown, entity = 'orders'): OrderDto[] {
  return asArray(raw, (item, index) => normalizeOrder(item, `${entity}[${index}]`));
}

function normalizeSupplierOrderLink(raw: unknown, entity: string): SupplierOrderLinkHintDto | null {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
    return null;
  }

  const orderProductId = asString(record.orderProductId);
  if (!orderProductId) {
    reportDtoAnomaly(entity, 'orderProductId', raw);
    return null;
  }

  if (record.ambiguous === true) {
    return { orderProductId, ambiguous: true };
  }

  return {
    orderProductId,
    ambiguous: false,
    supplierOrderId: asOptionalString(record.supplierOrderId) ?? null,
    supplierOrderDisplayNumber: asOptionalString(record.supplierOrderDisplayNumber) ?? null,
    supplierOrderItemId: asOptionalString(record.supplierOrderItemId) ?? null,
    unitPurchaseCostPLN: record.unitPurchaseCostPLN == null ? null : asNumber(record.unitPurchaseCostPLN),
    sourceUnitPurchaseCost: record.sourceUnitPurchaseCost == null ? null : asNumber(record.sourceUnitPurchaseCost),
    purchaseValuePLN: record.purchaseValuePLN == null ? null : asNumber(record.purchaseValuePLN),
    sourcePurchaseValue: record.sourcePurchaseValue == null ? null : asNumber(record.sourcePurchaseValue),
    purchaseQuantity: record.purchaseQuantity == null ? null : asNumber(record.purchaseQuantity),
    purchaseUnit: asOptionalString(record.purchaseUnit) ?? null,
    exchangeRateToPLN: record.exchangeRateToPLN == null ? null : asNumber(record.exchangeRateToPLN),
    exchangeRateToPLNSource: asOptionalString(record.exchangeRateToPLNSource) ?? null,
    exchangeRateToPLNTableNumber: asOptionalString(record.exchangeRateToPLNTableNumber) ?? null,
    exchangeRateToPLNRequestedDate: asOptionalString(record.exchangeRateToPLNRequestedDate) ?? null,
    exchangeRateToPLNEffectiveDate: asOptionalString(record.exchangeRateToPLNEffectiveDate) ?? null,
    sourceCurrency: asOptionalString(record.sourceCurrency) ?? null,
    allocatedQuantity: record.allocatedQuantity == null ? null : asNumber(record.allocatedQuantity),
    allocatedUnit: asOptionalString(record.allocatedUnit) ?? null,
    supplierOrderItemUnit: asOptionalString(record.supplierOrderItemUnit) ?? null,
  };
}

export function normalizeOrderOption(raw: unknown, entity = 'orderOption'): OrderOptionDto {
  const order = normalizeOrder(raw, entity);
  const record = asRecord(raw);
  // Only surface the hint array when the backend actually sent it — absence
  // (undefined) is the "no supplier-price visibility / not requested" signal the
  // transport form uses to fall back to exactly today's no-prefill behavior.
  const supplierOrderLinks = asOptionalArray(
    record?.supplierOrderLinks,
    (item, index) => normalizeSupplierOrderLink(item, `${entity}.supplierOrderLinks[${index}]`),
  )?.filter((link): link is SupplierOrderLinkHintDto => Boolean(link));

  return {
    id: order.id,
    orderNumber: order.orderNumber,
    displayOrderNumber: order.displayOrderNumber,
    clientOrderNumber: order.clientOrderNumber,
    customerName: order.customerName,
    label: [order.displayOrderNumber || order.orderNumber, order.customerName].filter(Boolean).join(' - '),
    status: order.status,
    revenue: order.revenue,
    currency: order.currency,
    description: order.description,
    factoryLocation: order.factoryLocation ?? null,
    deliveryLocation: order.deliveryLocation ?? null,
    customer: order.customer,
    products: order.products,
    productsLoaded: order.productsLoaded,
    ...(supplierOrderLinks !== undefined ? { supplierOrderLinks } : {}),
  };
}

export function normalizeOrderOptions(raw: unknown, entity = 'orderOptions'): OrderOptionDto[] {
  return asArray(raw, (item, index) => normalizeOrderOption(item, `${entity}[${index}]`));
}

export function normalizeCustomer(raw: unknown, entity = 'customer'): Customer | null {
  const record = asRecord(raw);

  if (!record) {
    if (raw != null) {
      reportDtoAnomaly(entity, 'shape', raw);
    }
    return null;
  }

  return {
    id: asString(record.id),
    name: asString(record.name),
    email: asOptionalString(record.email),
    phone: asOptionalString(record.phone),
    contactPerson: asOptionalString(record.contactPerson) ?? null,
    address: asOptionalString(record.address) ?? null,
    taxId: asOptionalString(record.taxId) ?? null,
    parentCustomerId: asOptionalString(record.parentCustomerId) ?? null,
    isBranch: record.isBranch === true,
    defaultPaymentTermDays: record.defaultPaymentTermDays == null
      ? DEFAULT_CUSTOMER_PAYMENT_TERM_DAYS
      : asNumber(record.defaultPaymentTermDays),
    customerCategoryId: asOptionalString(record.customerCategoryId) ?? null,
    customerCategoryName: asOptionalString(record.customerCategoryName) ?? null,
    customerCategoryColor: asOptionalString(record.customerCategoryColor) ?? null,
    createdAt: asOptionalString(record.createdAt),
    updatedAt: asOptionalString(record.updatedAt),
  };
}

export function normalizeUser(raw: unknown, entity = 'user'): User | null {
  const record = asRecord(raw);

  if (!record) {
    if (raw != null) {
      reportDtoAnomaly(entity, 'shape', raw);
    }
    return null;
  }

  return {
    id: asString(record.id),
    email: asString(record.email),
    name: asString(record.name),
    role: normalizeUserRole(record.role, `${entity}.role`),
    canManageOrderCosts: asBoolean(record.canManageOrderCosts),
    canManagePurchaseInvoices: asBoolean(record.canManagePurchaseInvoices),
    canManageSupplierOrders: asBoolean(record.canManageSupplierOrders),
    canViewCustomerSalePrices: asBoolean(record.canViewCustomerSalePrices),
    canEditCustomerSalePrices: asBoolean(record.canEditCustomerSalePrices),
    canViewSupplierPurchasePrices: asBoolean(record.canViewSupplierPurchasePrices),
    canEditSupplierPurchasePrices: asBoolean(record.canEditSupplierPurchasePrices),
    canViewSupplierOrderPurchasePrices: asBoolean(record.canViewSupplierOrderPurchasePrices),
    canEditSupplierOrderPurchasePrices: asBoolean(record.canEditSupplierOrderPurchasePrices),
    canViewSalesDocuments: asBoolean(record.canViewSalesDocuments),
    canViewPurchaseDocuments: asBoolean(record.canViewPurchaseDocuments),
    canViewFinancialReports: asBoolean(record.canViewFinancialReports),
    canViewDashboardFullKpis: asBoolean(record.canViewDashboardFullKpis),
    canViewCustomerPayments: asBoolean(record.canViewCustomerPayments),
    canViewTransportCosts: asBoolean(record.canViewTransportCosts),
    canEditTransportCosts: asBoolean(record.canEditTransportCosts),
    canViewMargin: asBoolean(record.canViewMargin),
    deletedAt: asOptionalString(record.deletedAt) ?? null,
  };
}

function normalizeOrderProduct(raw: unknown, entity: string): OrderProduct {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  const conversionUnit = normalizeConversionUnit(record?.conversionUnit);
  const persistedConversionQuantity = getPersistedQuantityForUnit(record, conversionUnit);

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    productVariantId: asOptionalString(record?.productVariantId),
    productCode: asOptionalString(record?.productCode),
    productEan: asOptionalString(record?.productEan),
    catalogProductName: asOptionalString(record?.catalogProductName),
    variantName: asOptionalString(record?.variantName),
    variantAttributes: normalizeVariantAttributes(record?.variantAttributes),
    variantIsDefault: record?.variantIsDefault == null ? undefined : asBoolean(record.variantIsDefault),
    catalogProductWidth: record?.catalogProductWidth == null ? undefined : asNumber(record?.catalogProductWidth),
    catalogProductHeight: record?.catalogProductHeight == null ? undefined : asNumber(record?.catalogProductHeight),
    catalogProductLength: record?.catalogProductLength == null ? undefined : asNumber(record?.catalogProductLength),
    catalogProductThickness: record?.catalogProductThickness == null ? undefined : asNumber(record?.catalogProductThickness),
    productCategoryName: asOptionalString(record?.productCategoryName),
    productCategoryColor: asOptionalString(record?.productCategoryColor),
    productCategoryDimensionDisplayProfile: asOptionalString(record?.productCategoryDimensionDisplayProfile),
    name: asString(record?.name),
    quantity: asNumber(record?.quantity),
    quantityPcs: record?.quantityPcs == null ? null : asNumber(record.quantityPcs),
    quantityM2: record?.quantityM2 == null ? null : asNumber(record.quantityM2),
    quantityM3: record?.quantityM3 == null ? null : asNumber(record.quantityM3),
    unit: asOptionalString(record?.unit),
    saleUnit: normalizeSaleUnit(record?.saleUnit, record?.unit),
    saleQuantity: record?.saleQuantity == null ? undefined : asNumber(record?.saleQuantity),
    conversionUnit,
    conversionQuantity: record?.conversionQuantity == null
      ? (persistedConversionQuantity == null ? undefined : asNumber(persistedConversionQuantity))
      : asNumber(record.conversionQuantity),
    unitPrice: record?.unitPrice == null ? undefined : asNumber(record?.unitPrice),
    totalPrice: record?.totalPrice == null ? undefined : asNumber(record?.totalPrice),
    currency: asOptionalString(record?.currency),
    description: asOptionalString(record?.description),
    cnCode: asOptionalString(record?.cnCode),
    netWeight: record?.netWeight == null ? undefined : asNumber(record?.netWeight),
    grossWeight: record?.grossWeight == null ? undefined : asNumber(record?.grossWeight),
    countryOfOrigin: asOptionalString(record?.countryOfOrigin),
    width: record?.width == null ? undefined : asNumber(record?.width),
    height: record?.height == null ? undefined : asNumber(record?.height),
    length: record?.length == null ? undefined : asNumber(record?.length),
    thickness: record?.thickness == null ? undefined : asNumber(record?.thickness),
    // Package-based LDM (logistics) defaults carried from the catalog Product
    // (orderRepository joins Product via ProductVariant). Without these the
    // transport form's cargo-row prefill has nothing to seed packaging from,
    // even though the API response carries them (browser-regression HIGH-2).
    piecesPerPackage: record?.piecesPerPackage == null ? undefined : asNumber(record?.piecesPerPackage),
    packageLength: record?.packageLength == null ? undefined : asNumber(record?.packageLength),
    packageWidth: record?.packageWidth == null ? undefined : asNumber(record?.packageWidth),
    packageStackLayers: record?.packageStackLayers == null ? undefined : asNumber(record?.packageStackLayers),
    createdAt: asOptionalString(record?.createdAt),
    updatedAt: asOptionalString(record?.updatedAt),
  };
}

function normalizeSaleUnit(value: unknown, fallback: unknown): QuantityUnit {
  const unit = typeof value === 'string' ? value : fallback;
  if ((READABLE_QUANTITY_UNITS as readonly unknown[]).includes(unit)) {
    return unit as QuantityUnit;
  }
  return 'pcs';
}

function normalizeCost(raw: unknown, entity: string): Cost {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    category: normalizeCostCategory(record?.category, `${entity}.category`),
    amount: asNumber(record?.amount),
    originalCurrency: asString(record?.originalCurrency, 'PLN'),
    originalAmount: record?.originalAmount == null ? undefined : asNumber(record?.originalAmount),
    exchangeRateUsed: record?.exchangeRateUsed == null ? undefined : asNumber(record?.exchangeRateUsed),
    exchangeRateSource: asOptionalString(record?.exchangeRateSource) ?? null,
    exchangeRateTableNumber: asOptionalString(record?.exchangeRateTableNumber) ?? null,
    exchangeRateRequestedDate: asOptionalString(record?.exchangeRateRequestedDate) ?? null,
    exchangeRateEffectiveDate: asOptionalString(record?.exchangeRateEffectiveDate) ?? null,
    exchangeRateIsFallback: record?.exchangeRateIsFallback == null ? null : asBoolean(record.exchangeRateIsFallback),
    supplierId: asOptionalString(record?.supplierId) ?? null,
    purchaseInvoiceNumber: asOptionalString(record?.purchaseInvoiceNumber) ?? null,
    purchaseInvoiceDate: asOptionalString(record?.purchaseInvoiceDate) ?? null,
    description: asString(record?.description),
    addedById: asString(record?.addedById),
    addedBy: normalizeUser(record?.addedBy, `${entity}.addedBy`),
    documentUrl: asOptionalString(record?.documentUrl),
    documentName: asOptionalString(record?.documentName),
    incurredAt: asString(record?.incurredAt),
    createdAt: asString(record?.createdAt),
    updatedAt: asString(record?.updatedAt),
  };
}

function normalizeStatusHistory(raw: unknown, entity: string): StatusHistory {
  const record = asRecord(raw);

  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    status: normalizeOrderStatus(record?.status, `${entity}.status`),
    notes: asOptionalString(record?.notes),
    changedAt: asString(record?.changedAt),
  };
}

export function normalizeOrderStatus(value: unknown, entity: string): OrderStatus {
  if (typeof value === 'string' && ORDER_STATUSES.includes(value as OrderStatus)) {
    return value as OrderStatus;
  }

  reportDtoAnomaly(entity, 'value', value);
  return 'NEW_INQUIRY';
}

function normalizeUserRole(value: unknown, entity: string): User['role'] {
  if (typeof value === 'string' && USER_ROLES.includes(value as User['role'])) {
    return value as User['role'];
  }

  reportDtoAnomaly(entity, 'value', value);
  return 'OPERATIONS';
}

function normalizeCostCategory(value: unknown, entity: string): Cost['category'] {
  const allowed: Cost['category'][] = [
    'PRODUCT_COST',
    'TRANSPORT',
    'CUSTOMS_AGENCY',
    'BORDER_TAX',
    'MARKETING_BONUS',
    'OTHER',
  ];

  if (typeof value === 'string' && allowed.includes(value as Cost['category'])) {
    return value as Cost['category'];
  }

  reportDtoAnomaly(entity, 'value', value);
  return 'OTHER';
}

export function pickCustomerName(record: Pick<OrderDto, 'customerName'> | null | undefined): string {
  return record?.customerName || '';
}

export function pickOrderLabel(order: Pick<OrderOptionDto, 'displayOrderNumber' | 'customerName'>): string {
  return [order.displayOrderNumber, order.customerName].filter(Boolean).join(' - ');
}
